import * as React from "react";
import getPriceWithDecimalFromTotalAmountStripe from "payments/getPriceWithDecimalFromTotalAmountStripe";
import getStripe from "payments/getStripe";
import { postPaymentAPI } from "api/PaymentsAPI";
import { ProductKeys } from "containers/Pricing/products";
import BusinessProfileModel from "models/business-profiles/BusinessProfile";
import { ProductContext } from "@context";

export const useProductPayment = (productKey: ProductKeys) => {
	const { productWithPrices } = React.useContext(ProductContext);
	const product = productWithPrices.find((product) => product.productName === productKey);
	const [productPaymentState, setProductPaymentState] = React.useState({
		productPriceWithDecimal: getPriceWithDecimalFromTotalAmountStripe(String(product?.price)),
		isCheckoutLoading: false,
		hasCheckoutError: false,
	});

	const onCheckoutLoading = () => {
		setProductPaymentState({
			...productPaymentState,
			isCheckoutLoading: true,
			hasCheckoutError: false,
		});
	};

	const onCheckoutFails = () => {
		setProductPaymentState({
			...productPaymentState,
			isCheckoutLoading: false,
			hasCheckoutError: true,
		});
	};

	const onCheckoutFinishSuccessfully = () => {
		setProductPaymentState({
			...productPaymentState,
			isCheckoutLoading: false,
			hasCheckoutError: false,
		});
	};

	const checkout = async (businessProfile: BusinessProfileModel, quantity: number) => {
		// Create a Checkout Session.
		const [stripe, paymentResponse] = await Promise.all([
			getStripe(),
			postPaymentAPI(
				productPaymentState.productPriceWithDecimal,
				String(product?.productName),
				quantity,
				{
					id: Number(businessProfile.info.id),
					fullName: businessProfile.info.fullName,
				},
				businessProfile.info.url,
			),
		]);
		const paymentResponseJSON = await paymentResponse.json();

		if (paymentResponseJSON.statusCode === 500) {
			onCheckoutFails();
			return;
		}

		const { error } = await stripe!!.redirectToCheckout({
			sessionId: paymentResponseJSON.id,
		});

		if (error.message) {
			onCheckoutFails();
			return;
		}

		onCheckoutFinishSuccessfully();
	};

	const handleOnCheckout = async (e: any, businessProfile: BusinessProfileModel, quantity: number = 1) => {
		e.preventDefault();
		onCheckoutLoading();
		try {
			await checkout(businessProfile, quantity);
		} catch {
			onCheckoutFails();
		}
	};

	return {
		product,
		onCheckout: handleOnCheckout,
		...productPaymentState,
	};
};
